import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BRAND_NAME, BRAND_GITHUB_URL } from '../constants/brand';

const codingLinks = [
  { name: 'Github', url: BRAND_GITHUB_URL, icon: 'bi-github', label: 'CATSMOKER GitHub profile', color: '#24292F', border: '1px solid rgba(88, 166, 255, 0.55)' },
  { name: 'Discord', url: 'https://discord.com/invite/HQC5BwcXtS', icon: 'bi-discord', label: 'Discord community', color: '#5865F2' },
  { name: 'Telegram', url: 'https://t.me/CATSM0KER', icon: 'bi-telegram', label: 'Telegram community', color: '#005B84' },
];

// Styled Components
const Section = styled.section`
  background: ${props => props.theme.colors.cardBg};
  padding: ${props => props.theme.ui.cardPadding};
  border-radius: ${props => props.theme.ui.cardRadius};
  box-shadow: ${props => props.theme.shadows.md};
  border: 2px solid ${props => props.theme.colors.cardBorder};
  animation: fadeIn calc(0.7s * var(--motion-duration-scale, 1)) ${props => props.theme.transitions.medium} forwards;
  text-align: center;

  @keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
`;

const Content = styled.div``;

const ProfilePic = styled.img`
  width: clamp(120px, 20vw, 140px);
  height: clamp(120px, 20vw, 140px);
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  margin-bottom: ${props => props.theme.spacing.md};
  transition: transform calc(0.3s * var(--motion-duration-scale, 1)) ease, box-shadow calc(0.3s * var(--motion-duration-scale, 1)) ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    width: 104px;
    height: 104px;
    margin-bottom: ${props => props.theme.spacing.sm};
  }
`;

const ProfileVideo = styled.video`
  width: clamp(120px, 20vw, 140px);
  height: clamp(120px, 20vw, 140px);
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  margin-bottom: ${props => props.theme.spacing.md};
  transition: transform calc(0.3s * var(--motion-duration-scale, 1)) ease, box-shadow calc(0.3s * var(--motion-duration-scale, 1)) ease;
  object-fit: cover;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    width: 104px;
    height: 104px;
    margin-bottom: ${props => props.theme.spacing.sm};
  }
`;

const Name = styled.h1`
  font-size: clamp(1.7rem, 4vw, 2.1rem);
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const Welcome = styled.p`
  font-size: clamp(1.1rem, 3vw, 1.4rem);
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.textLighter};
`;

const Description = styled.p`
  font-size: clamp(0.9rem, 2vw, 1rem);
  font-weight: 400;
  line-height: 1.6;
  color: ${props => props.theme.colors.textLighter};
  margin-top: ${props => props.theme.spacing.md};
`;

const SocialLinks = styled.section`
  margin-top: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.md};

  @media (max-width: 480px) {
    margin-top: ${props => props.theme.spacing.md};
    gap: ${props => props.theme.spacing.sm};
  }
`;

const Subtitle = styled.h2`
  font-size: clamp(1rem, 2.5vw, 1.1rem);
  font-weight: 500;
  color: ${props => props.theme.colors.textLighter};
  margin-bottom: ${props => props.theme.spacing.md};
  text-align: center;

  @media (max-width: 480px) {
    margin-bottom: ${props => props.theme.spacing.sm};
  }
`;

const SocialGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${props => props.theme.spacing.md};

  @media (max-width: 480px) {
    gap: 5px;
  }
`;

const LinkButton = styled.a`
  text-decoration: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: 50px;
  font-size: clamp(0.75rem, 1.8vw, 0.85rem);
  font-weight: 600;
  color: ${props => props.theme.colors.white};
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  transition: transform ${props => props.theme.transitions.medium}, box-shadow ${props => props.theme.transitions.medium};
  border: ${props => props.$border || `1px solid ${props.theme.colors.cardBorder}`};
  background: ${props => props.$color || 'rgba(255, 255, 255, 0.1)'};

  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: ${props => props.theme.shadows.lg};
  }

  i {
    font-size: clamp(0.85rem, 2vw, 1rem);
  }

  @media (max-width: 480px) {
    padding-left: 8px;
    padding-right: 8px;
  }
`;

// (Toggle removed — site focuses on coding/gaming)

const ProfileSection = ({ activeSection, setActiveSection }) => {
  const { t } = useTranslation();
  const links = codingLinks;

  return (
    <Section id="home-section">
      <Content>
        {/* Use video if available (much smaller file size), fallback to GIF */}
        <ProfileVideo
          autoPlay
          loop
          muted
          playsInline
          poster="/assets/images/logo.webp"
          aria-hidden="true"
          tabIndex={-1}
          onError={(e) => {
            // Fallback to GIF if video fails to load
            e.target.style.display = 'none';
            e.target.nextElementSibling.style.display = 'block';
          }}
        >
          <source src="/assets/images/logo.mp4" type="video/mp4" />
        </ProfileVideo>
        <ProfilePic
          src="/assets/images/logo.gif"
          alt={`${BRAND_NAME} Logo`}
          width="94"
          height="94"
          style={{ display: 'none' }}
        />
        <Name>
          {t('profile.hi')}
        </Name>
        <Welcome>{t('profile.welcome_coding')}</Welcome>
        <Description>
          {t('profile.description_coding').split('<br />').map((line, index) => (
            <React.Fragment key={line}>
              {index > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </Description>

      </Content>
      <SocialLinks>
        <Subtitle>{t('profile.connect')}</Subtitle>
        <SocialGrid>
          {links.map(link => (
            <LinkButton
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              $color={link.color}
              $border={link.border}
              aria-label={link.label}
            >
              <i className={link.icon}></i>
              <span>{link.name}</span>
            </LinkButton>
          ))}
        </SocialGrid>
      </SocialLinks>
    </Section>
  );
};

export default ProfileSection;